import React, { FC, useEffect, useState } from 'react';
import Select, { ActionMeta, CSSObjectWithLabel, MultiValue, Props as SelectProps, SingleValue } from 'react-select';
import { mergeSelectStyles } from './SelectStyles';

export interface PickerOption {
  value: string;
  label: string;
}

interface PickerProps extends SelectProps {
  label?: string;
  onSelectionChange?: (selectedOption: SingleValue<any>) => void;
  value?: string;
  options?: PickerOption[];
  singleValueOptions?:string[];
  disabled?: boolean;
  className?:string;
  updateDefault?:boolean;
  enumOptions?: Record<string, string>; // Add this to accept an enum
  error?: string;
}

const Picker: FC<PickerProps> = ({ label, name, onSelectionChange, value, options, disabled,updateDefault, error, id, ...props }) => {
  const [selectedOption, setSelectedOption] = useState<SingleValue<PickerOption> | null>(null);
  useEffect(() => {
    if (options) {
      const initialOption = (options as PickerOption[]).find(option => option.value === value) || null;
      setSelectedOption(initialOption);
      if(updateDefault && onSelectionChange) onSelectionChange(initialOption)
    }
  }, [value, options]);
  const handleSelectChange = (selectedOption: SingleValue<PickerOption> | MultiValue<PickerOption>, _actionMeta: ActionMeta<PickerOption>) => {
    const option = selectedOption as SingleValue<PickerOption>;
    setSelectedOption(option);
    if(onSelectionChange){
      onSelectionChange(option)
    }
  };
  const formatOptionLabel = ({ label }: PickerOption) => (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {label.toTranslatedDisplay()}
    </div>
  );

  return (
    <div className='' id={id || name}>
      {label &&(
        <label className='form-label'>{label}</label>
      )}
      <Select className='react-select-styled'
      classNamePrefix='react-select'
      options={options}
      formatOptionLabel={formatOptionLabel}
      value={selectedOption}
      isDisabled={disabled}
      onChange={handleSelectChange}
      menuPortalTarget={document.body}
      menuPosition="fixed"
      styles={mergeSelectStyles({
        control: (base: CSSObjectWithLabel) => ({
          ...base,
          // !important needed: react-select's own SCSS beats this emotion class on
          // specificity otherwise, see AccountPicker.tsx (MBAI-160).
          borderColor: error ? '#f1416c !important' : base.borderColor,
        }),
      })} />
      {error && <div className="invalid-feedback d-block">{error}</div>}
    </div>
  );
};

export const convertEnumToPickerOptions = <T extends Record<string, string | number>>(
  enumObj: T,
): PickerOption[] => {
  return Object.keys(enumObj)
    .filter(key => typeof enumObj[key as keyof T] === "string") // Filter out numeric enum values if any
    .map(key => ({
      value: key,
      label: key+'A' //find with key,
    }));
};


export const EasyPicker: FC<PickerProps> = ({ label, name, onSelectionChange, value, options, singleValueOptions, enumOptions, updateDefault,className, ...props }) => {
  const [selectedOption, setSelectedOption] = useState<SingleValue<PickerOption> | null>(null);
  const [pickerOptions, setPickerOptions] = useState<PickerOption[]>([]);

  useEffect(() => {
    if (options) {
      setPickerOptions(options);
    } else if (enumOptions) {
      const enumPickerOptions = Object.keys(enumOptions).map(key => ({
        value: key,
        label: enumOptions[key],
      }));
      setPickerOptions(enumPickerOptions);
    } else if (singleValueOptions) {
      const singleValuePickerOptions = singleValueOptions.map(option => ({
        value: option,
        label: option,
      }));
      setPickerOptions(singleValuePickerOptions);
    }
  }, [options, enumOptions, singleValueOptions]);
  

  useEffect(() => {
    if (value && pickerOptions.length > 0) {
      const initialOption = pickerOptions.find(option => option.value === value) || null;
      if(updateDefault) handleSelectChange(initialOption, {} as ActionMeta<PickerOption>)
      setSelectedOption(initialOption);
    }
  }, [value, pickerOptions]);

  const handleSelectChange = (selectedOption: SingleValue<PickerOption> | MultiValue<PickerOption>, _actionMeta: ActionMeta<PickerOption>) => {
    const option = selectedOption as SingleValue<PickerOption>;
    setSelectedOption(option);
    if (onSelectionChange && option) {
      onSelectionChange({
        target: {
          name,
          value: option.value,
        },
      } as React.ChangeEvent<HTMLInputElement>);
    }
  };

  const formatOptionLabel = ({ label }: PickerOption) => (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {label.toTranslatedDisplay()}
    </div>
  );

  return (
    <div className={className}>
      {label && <label className="form-label">{label}</label>}
      <Select
        className="react-select-styled"
        classNamePrefix="react-select"
        options={pickerOptions}
        formatOptionLabel={formatOptionLabel}
        value={selectedOption}
        onChange={handleSelectChange}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        styles={mergeSelectStyles()}
      />
    </div>
  );
};




export default Picker;
